import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import LoadingOrbit from "../components/LoadingOrbit";
import PageContent from "../components/shared/PageContent";
import AnalyticalDecisionJourney from "../components/analytical_journey/AnalyticalDecisionJourney";
import usePlatformSettings from "../hooks/usePlatformSettings";
import useActiveFairScenarios from "../hooks/risk_financing/useActiveFairScenarios";
import { usePortfolioAnalysis } from "../modules/portfolio_decision/portfolio_analysis/hooks/usePortfolioAnalysis";
import PortfolioDecisionCenter from "../modules/portfolio_decision/pages/PortfolioDecisionCenter";
import {
  RiskFinancingProvider,
  useRiskFinancingContext,
} from "../modules/risk_financing";
import { aggregateDecisionCenterData } from "../modules/portfolio_decision/decision_center";
import useInvestmentGovernancePosition from "../modules/investment_governance/process/useInvestmentGovernancePosition";
import { adaptInvestmentGovernanceState } from "../modules/portfolio_decision/decision_center/adapters";
import { valueRealizationContextAdapter } from "../modules/portfolio_decision/decision_center/adapters";
import useDecisionAnalytics from "../modules/portfolio_decision/decision_center/hooks/useDecisionAnalytics";
import { getFairSeedConfig } from "../lib/platformSettings";
import { usePortfolioOptimizationEvidence } from "../modules/portfolio_decision/portfolio_analysis/context/PortfolioOptimizationEvidenceContext";
import ManagementProposalContext from "../modules/portfolio_decision/components/ManagementProposalContext";

const DecisionCenterPage = () => {
  const { settings, settingsLoaded, settingsError } = usePlatformSettings();
  const simulations = Number(settings?.simulations ?? 10000);

  if (settingsError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Unable to load Platform Settings.
        </div>
      </div>
    );
  }

  if (!settingsLoaded) return <LoadingOrbit />;

  return (
    <RiskFinancingProvider
      settingsLoaded={settingsLoaded}
      simulations={simulations}
      strategyAnalysisEnabled={false}
      initialTab="portfolio"
    >
      <DecisionCenterContent settings={settings} simulations={simulations} />
    </RiskFinancingProvider>
  );
};

export const DecisionCenterContent = ({ settings, simulations }) => {
  const [portfolioDecision, setPortfolioDecision] = useState(null);
  const [valueRealizationContext, setValueRealizationContext] = useState(null);
  const riskFinancingEngine = useRiskFinancingContext();
  const governancePosition = useInvestmentGovernancePosition();
  const decisionAnalytics = useDecisionAnalytics();
  const { scenarios, loading: scenariosLoading } = useActiveFairScenarios({ enabled: true });
  const { getDecisionEvidence } = usePortfolioOptimizationEvidence();
  const scenarioIds = (scenarios || []).map(({ id }) => id);
  const seed = getFairSeedConfig(settings).seed;
  const modeledEconomicOptimum = getDecisionEvidence({ scenarioIds, simulations, seed, seedMode: settings?.seedMode, currency: settings?.currency, economicHorizonYears: settings?.economicHorizonYears });
  useEffect(() => {
    let active = true;
    axios.get("/api/digital-risk/benefits-governance/decision-center/value-realization-context/", { withCredentials: true })
      .then(({ data }) => active && setValueRealizationContext(valueRealizationContextAdapter(data)))
      .catch(() => active && setValueRealizationContext({ status: "UNAVAILABLE", reason: "UNAVAILABLE" }));
    return () => { active = false; };
  }, []);

  // Preserve the existing authoritative Portfolio Analysis request and its
  // unconstrained/constrained optimization semantics.
  const portfolioAnalysis = usePortfolioAnalysis({
    scenarioIds,
    simulations,
    seed,
    objective: "CAPITAL_AT_RISK_REDUCTION",
    optimizationMode: "CAPITAL_ALLOCATION",
    enabled: !scenariosLoading && (scenarios || []).length > 0,
  });

  const authoritativePortfolioData = portfolioDecision ?? portfolioAnalysis.data;
  const decisionCenterContext = useMemo(
    () => decisionAnalytics.status !== "loading"
      ? aggregateDecisionCenterData({
          decisionAnalytics: decisionAnalytics.data,
          portfolioData: authoritativePortfolioData,
          scenarioData: null,
          financingEngine: riskFinancingEngine,
          governanceData: adaptInvestmentGovernanceState(governancePosition),
          valueRealizationData: valueRealizationContext,
        })
      : null,
    [decisionAnalytics, authoritativePortfolioData, riskFinancingEngine, governancePosition, valueRealizationContext]
  );

  return (
    <PageContent className="min-h-full bg-slate-50" data-page-content-owner="portfolio-capital-strategy" data-testid="decision-center-content">
      <header className="mb-5 bg-slate-50">
        <h1 className="text-2xl font-semibold text-slate-950">Portfolio Capital Strategy</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Define the capital strategy for digital risk by comparing the economic opportunity with the most efficient allocation of available capital.
        </p>
      </header>

      <div className="mb-5">
        <AnalyticalDecisionJourney currentStage="propose" />
      </div>

      {decisionAnalytics.status === "unavailable" && (
        <div role="alert" className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Authoritative decision analytics are unavailable. Analytical positions are not being reconstructed from legacy sources.
        </div>
      )}

      <ManagementProposalContext decision={modeledEconomicOptimum} currency={settings?.currency} />

      {decisionCenterContext ? (
        <PortfolioDecisionCenter
          embedded
          settings={settings}
          activeScenarios={scenarios}
          decisionCenterContext={decisionCenterContext}
          portfolioAnalysisLoading={portfolioAnalysis.loading && !portfolioDecision}
          portfolioAnalysisError={portfolioDecision ? null : portfolioAnalysis.error}
          acceptedOptimization={modeledEconomicOptimum}
          onDecisionGenerated={setPortfolioDecision}
        />
      ) : (
        <LoadingOrbit />
      )}
    </PageContent>
  );
};

export default DecisionCenterPage;
